import React from 'react';
import { Checkbox } from '@frontend/shadcn/components/ui/checkbox';
import {
  CommandSelect,
  CommandSelectTrigger,
  CommandSelectValue,
  CommandSelectContent,
  CommandSelectEmpty,
  CommandSelectLoading,
  CommandSelectItem,
  CommandSelectAll,
  useCommandSelectSearch,
} from '../../Analytics/components/filters-form/command-select';

// Auto-select keywords for campaigns and ad sets
const DEFAULT_AUTO_SELECT_KEYWORDS = ['UGC', 'INFLUENCER', 'ASPIRE'] as const;

// Enhanced performance constants for large datasets
const CACHE_SIZE_LIMIT = 1000; // Increased for better cache hit rates
const DEBOUNCE_DELAY = 150; // Optimized for responsiveness
const BATCH_SIZE = 100; // Increased for better throughput
const SEARCH_MIN_LENGTH = 1; // Reduced for better UX
const FILTER_BATCH_DELAY = 16; // ~60fps

// Types for Ad Campaigns and Ad Sets
interface AdCampaign {
  id: string;
  name: string;
  adSets?: AdSet[];
  hasPaidPartnershipAds?: boolean;
}

interface AdSet {
  id: string;
  name: string;
  hasPaidPartnershipAds?: boolean;
}

// Enhanced cache entry with metadata
interface CacheEntry {
  value: boolean;
  timestamp: number;
  accessCount: number;
}

// Combined type for display
interface AdCampaignsAndSetsSelectProps {
  children: React.ReactNode;
}

export const AdCampaignsAndSetsSelect: React.FC<AdCampaignsAndSetsSelectProps> = ({ children }) => {
  return <div className="relative">{children}</div>;
};

// Enhanced debouncing hook with better performance
const useDebounce = (value: string, delay: number = DEBOUNCE_DELAY) => {
  const [debouncedValue, setDebouncedValue] = React.useState(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Advanced caching hook with LFU (Least Frequently Used) eviction
const useAdvancedCache = () => {
  const cache = React.useRef(new Map<string, CacheEntry>());

  const get = React.useCallback((key: string): boolean | null => {
    const entry = cache.current.get(key);
    if (!entry) return null;

    // Update access metadata
    entry.accessCount++;
    entry.timestamp = Date.now();

    return entry.value;
  }, []);

  const set = React.useCallback((key: string, value: boolean) => {
    // LFU eviction when cache is full
    if (cache.current.size >= CACHE_SIZE_LIMIT) {
      let minAccessCount = Infinity;
      let oldestKey = '';
      let oldestTimestamp = Infinity;

      // Find least frequently used and oldest entry
      for (const [cacheKey, entry] of cache.current.entries()) {
        if (
          entry.accessCount < minAccessCount ||
          (entry.accessCount === minAccessCount && entry.timestamp < oldestTimestamp)
        ) {
          minAccessCount = entry.accessCount;
          oldestKey = cacheKey;
          oldestTimestamp = entry.timestamp;
        }
      }

      if (oldestKey) {
        cache.current.delete(oldestKey);
      }
    }

    cache.current.set(key, { value, timestamp: Date.now(), accessCount: 1 });
  }, []);

  return { get, set };
};

// Enhanced auto-select hook with advanced caching
const useShouldAutoSelect = () => {
  const { get, set } = useAdvancedCache();

  return React.useCallback(
    (name: string, keywords: readonly string[] = DEFAULT_AUTO_SELECT_KEYWORDS): boolean => {
      const cacheKey = `${name}:${keywords.join(',')}`;

      // Check cache first
      const cached = get(cacheKey);
      if (cached !== null) return cached;

      // Compute result
      const nameUpper = name.toUpperCase();
      const result = keywords.some((keyword) => nameUpper.includes(keyword));

      // Cache result
      set(cacheKey, result);
      return result;
    },
    [get, set],
  );
};

// Optimized helper function with early termination for large datasets
export const getDefaultSelectedCampaignsAndSets = (
  adCampaigns: AdCampaign[],
  autoSelectKeywords: readonly string[] = DEFAULT_AUTO_SELECT_KEYWORDS,
): string[] => {
  const defaultSelected: string[] = [];
  const keywordsSet = new Set(autoSelectKeywords.map((k) => k.toUpperCase()));

  // Early termination if no campaigns
  if (!adCampaigns.length) return defaultSelected;

  // Pre-compute keyword patterns for better performance
  const keywordPatterns = Array.from(keywordsSet);

  for (const campaign of adCampaigns) {
    // Check if campaign should be auto-selected
    const campaignNameUpper = campaign.name.toUpperCase();
    const campaignNameMatch = keywordPatterns.some((keyword) => campaignNameUpper.includes(keyword));
    const campaignHasPaidPartnership = campaign.hasPaidPartnershipAds;
    const campaignShouldAutoSelect = campaignNameMatch || campaignHasPaidPartnership;

    if (campaign.adSets) {
      for (const adSet of campaign.adSets) {
        // Auto-select ad set if conditions are met
        const adSetNameUpper = adSet.name.toUpperCase();
        const adSetNameMatch = keywordPatterns.some((keyword) => adSetNameUpper.includes(keyword));
        const adSetHasPaidPartnership = adSet.hasPaidPartnershipAds;

        if (adSetNameMatch || campaignShouldAutoSelect || adSetHasPaidPartnership) {
          defaultSelected.push(adSet.id);
        }
      }
    }
  }

  return defaultSelected;
};

// Pre-computed search index for ultra-fast filtering
const createSearchIndex = (campaigns: AdCampaign[]) => {
  const index = new Map<
    string,
    {
      campaign: AdCampaign;
      adSet?: AdSet;
      campaignNameLower?: string;
      adSetNameLower?: string;
    }
  >();

  campaigns.forEach((campaign) => {
    // Precompute lowercase campaign name
    const campaignNameLower = campaign.name?.toLowerCase();

    // Index campaign name
    index.set(campaign.id, {
      campaign,
      campaignNameLower,
    });

    // Index ad set names
    campaign.adSets?.forEach((adSet) => {
      // Precompute lowercase ad set name
      const adSetNameLower = adSet.name?.toLowerCase();

      index.set(adSet.id, {
        campaign,
        adSet,
        campaignNameLower,
        adSetNameLower,
      });
    });
  });

  return index;
};

// Advanced filtering with search index and batch processing
const useAdvancedFiltering = (campaigns: AdCampaign[], searchQuery: string) => {
  const [filteredCampaigns, setFilteredCampaigns] = React.useState<AdCampaign[]>([]);
  const [isFiltering, setIsFiltering] = React.useState(false);

  // Memoize search index
  const searchIndex = React.useMemo(() => createSearchIndex(campaigns), [campaigns]);

  // Add search results cache
  const searchResultsCache = React.useRef(new Map<string, AdCampaign[]>());

  const performFiltering = React.useCallback(
    (query: string) => {
      if (!query || query.length < SEARCH_MIN_LENGTH) {
        setFilteredCampaigns([]);
        setIsFiltering(false);
        return;
      }

      // Check cache first
      const cachedResults = searchResultsCache.current.get(query);
      if (cachedResults) {
        setFilteredCampaigns(cachedResults);
        setIsFiltering(false);
        return;
      }
      setIsFiltering(true);
      const queryLower = query.toLowerCase();

      // Use requestIdleCallback for non-blocking filtering
      const filterBatch = () => {
        const matchingCampaigns = new Map<string, AdCampaign>();

        // Process in batches to avoid blocking UI
        const processBatch = (startIndex: number) => {
          const campaignEntries = Array.from(searchIndex.values());
          const endIndex = Math.min(startIndex + BATCH_SIZE, campaignEntries.length);

          for (let i = startIndex; i < endIndex; i++) {
            const { campaign, adSet, campaignNameLower, adSetNameLower } = campaignEntries[i];

            if (adSet) {
              // Check ad set name using precomputed lowercase value
              if (adSetNameLower?.includes(queryLower)) {
                const existing = matchingCampaigns.get(campaign.id);
                if (existing) {
                  existing.adSets = existing.adSets || [];
                  if (!existing.adSets.find((as) => as.id === adSet.id)) {
                    existing.adSets.push(adSet);
                  }
                } else {
                  matchingCampaigns.set(campaign.id, {
                    ...campaign,
                    adSets: [adSet],
                  });
                }
              }
            } else {
              // Check campaign name using precomputed lowercase value
              if (campaignNameLower?.includes(queryLower)) {
                matchingCampaigns.set(campaign.id, campaign);
              }
            }
          }

          // Continue processing if more items
          if (endIndex < campaignEntries.length) {
            setTimeout(() => processBatch(endIndex), FILTER_BATCH_DELAY);
          } else {
            const results = Array.from(matchingCampaigns.values());

            // Cache the results
            if (searchResultsCache.current.size >= 50) {
              // Simple LRU eviction - remove oldest entry
              const firstKey = searchResultsCache.current.keys().next().value;
              searchResultsCache.current.delete(firstKey);
            }
            searchResultsCache.current.set(query, results);
            setFilteredCampaigns(results);
            setIsFiltering(false);
          }
        };

        processBatch(0);
      };

      if ('requestIdleCallback' in window) {
        requestIdleCallback(filterBatch, { timeout: 100 });
      } else {
        setTimeout(filterBatch, 0);
      }
    },
    [searchIndex],
  );

  // Clear cache when campaigns change
  React.useEffect(() => {
    searchResultsCache.current.clear();
  }, [campaigns]);

  React.useEffect(() => {
    performFiltering(searchQuery);
  }, [searchQuery, performFiltering]);

  return { filteredCampaigns, isFiltering };
};

// Memoized Campaign Group Header with enhanced performance
const CampaignGroupHeader: React.FC<{
  campaign: AdCampaign;
  selectedIdsSet: Set<string>;
  onToggleCampaign: (campaignId: string) => void;
  isUpdating: boolean;
  autoSelectKeywords: readonly string[];
}> = React.memo(({ campaign, selectedIdsSet, onToggleCampaign, isUpdating, autoSelectKeywords }) => {
  const memoizedShouldAutoSelect = useShouldAutoSelect();

  // Enhanced memoization with more granular dependencies
  const { adSetIds, selectedAdSetIds, isAllSelected, shouldAutoSelectCampaign, campaignHasPaidPartnership } =
    React.useMemo(() => {
      const adSetIds = campaign.adSets?.map((adSet) => adSet.id) || [];

      // Use Set intersection for better performance
      const selectedAdSetIds = adSetIds.filter((id) => selectedIdsSet.has(id));
      const isAllSelected = adSetIds.length > 0 && selectedAdSetIds.length === adSetIds.length;
      const shouldAutoSelectCampaign = memoizedShouldAutoSelect(campaign.name, autoSelectKeywords);
      const campaignHasPaidPartnership = campaign.hasPaidPartnershipAds;

      return {
        adSetIds,
        selectedAdSetIds,
        isAllSelected,
        shouldAutoSelectCampaign,
        campaignHasPaidPartnership,
      };
    }, [
      campaign.name,
      campaign.adSets,
      campaign.hasPaidPartnershipAds,
      selectedIdsSet,
      autoSelectKeywords,
      memoizedShouldAutoSelect,
    ]);

  return (
    <div className="flex items-center gap-2 px-2 py-2 text-sm font-medium text-foreground border-b">
      <Checkbox
        checked={isAllSelected}
        onCheckedChange={() => onToggleCampaign(campaign.id)}
        disabled={isUpdating || adSetIds.length === 0}
        aria-label={`Select all ad sets in ${campaign.name} campaign`}
      />
      <span className="flex-1">{campaign.name}</span>
      {(shouldAutoSelectCampaign || campaignHasPaidPartnership) && (
        <span className="text-xs text-muted-foreground">
          ⭐ Auto-selected{campaignHasPaidPartnership ? ' (Paid Partnership)' : ''}
        </span>
      )}
      <span className="text-xs text-muted-foreground">
        ({selectedAdSetIds.length}/{adSetIds.length})
      </span>
    </div>
  );
});

CampaignGroupHeader.displayName = 'CampaignGroupHeader';

// Optimized content component with advanced filtering and virtual scrolling
const AdCampaignsAndSetsContent: React.FC<{
  adCampaigns: AdCampaign[];
  isLoading: boolean;
  selectedIds: string[];
  isUpdating: boolean;
  handleSelectItem: (itemId: string) => void;
  updateSelection: (selection: string[]) => void;
  autoSelectKeywords: readonly string[];
}> = React.memo(
  ({ adCampaigns, isLoading, selectedIds, isUpdating, handleSelectItem, updateSelection, autoSelectKeywords }) => {
    const memoizedShouldAutoSelect = useShouldAutoSelect();

    // Get search query for filtering
    const searchQuery = useCommandSelectSearch();
    const debouncedSearchQuery = useDebounce(searchQuery, DEBOUNCE_DELAY);

    // Enhanced filtering with search index
    const { filteredCampaigns, isFiltering } = useAdvancedFiltering(adCampaigns, debouncedSearchQuery);

    // Memoize selectedIds as Set for O(1) lookups
    const selectedIdsSet = React.useMemo(() => new Set(selectedIds), [selectedIds]);

    // Memoize all visible ad set IDs calculation with Set for better performance
    const allVisibleIds = React.useMemo(() => {
      const ids: string[] = [];
      for (const campaign of filteredCampaigns) {
        if (campaign.adSets) {
          for (const adSet of campaign.adSets) {
            ids.push(adSet.id);
          }
        }
      }
      return ids;
    }, [filteredCampaigns]);

    // Memoize allVisibleIds as Set for O(1) lookups
    const allVisibleIdsSet = React.useMemo(() => new Set(allVisibleIds), [allVisibleIds]);

    // Use refs to store latest values for stable callbacks
    const selectedIdsRef = React.useRef(selectedIds);
    const allVisibleIdsRef = React.useRef(allVisibleIds);

    // Update refs without causing re-renders
    React.useEffect(() => {
      selectedIdsRef.current = selectedIds;
    }, [selectedIds]);

    React.useEffect(() => {
      allVisibleIdsRef.current = allVisibleIds;
    }, [allVisibleIds]);

    // Enhanced campaign toggle handler with Set-based operations
    const handleToggleCampaign = React.useCallback(
      (campaignId: string) => {
        if (isUpdating) return;

        const campaign = filteredCampaigns.find((c) => c.id === campaignId);
        if (!campaign?.adSets) return;

        const adSetIds = campaign.adSets.map((adSet) => adSet.id);
        const selectedAdSetIds = adSetIds.filter((id) => selectedIdsSet.has(id));

        let newSelection: string[];

        if (selectedAdSetIds.length === adSetIds.length) {
          // Deselect all ad sets in this campaign
          const adSetIdsSet = new Set(adSetIds);
          newSelection = selectedIds.filter((id) => !adSetIdsSet.has(id));
        } else {
          // Select all ad sets in this campaign
          const newSelectionSet = new Set(selectedIds);
          adSetIds.forEach((id) => newSelectionSet.add(id));
          newSelection = Array.from(newSelectionSet);
        }

        updateSelection(newSelection);
      },
      [filteredCampaigns, selectedIds, selectedIdsSet, updateSelection, isUpdating],
    );

    // Enhanced select all handler with better performance
    const wrappedHandleSelectAll = React.useCallback(() => {
      if (isUpdating || isLoading) return;

      const currentSelectedIds = selectedIdsRef.current;
      const currentAllVisibleIds = allVisibleIdsRef.current;
      const currentAllVisibleIdsSet = new Set(currentAllVisibleIds);
      const selectedVisibleIds = currentSelectedIds.filter((id) => currentAllVisibleIdsSet.has(id));

      let newSelection: string[];

      if (selectedVisibleIds.length === currentAllVisibleIds.length && currentAllVisibleIds.length > 0) {
        newSelection = currentSelectedIds.filter((id) => !currentAllVisibleIdsSet.has(id));
      } else {
        const newSelectionSet = new Set(currentSelectedIds);
        currentAllVisibleIds.forEach((id) => newSelectionSet.add(id));
        newSelection = Array.from(newSelectionSet);
      }

      updateSelection(newSelection);
    }, [updateSelection, isUpdating, isLoading]);

    // Memoized select all state calculation
    const selectAllState = React.useMemo(() => {
      const selectedVisibleIds = selectedIds.filter((id) => allVisibleIdsSet.has(id));
      return {
        checked: allVisibleIds.length > 0 && selectedVisibleIds.length === allVisibleIds.length,
        indeterminate: selectedVisibleIds.length > 0 && selectedVisibleIds.length < allVisibleIds.length,
      };
    }, [allVisibleIds, allVisibleIdsSet, selectedIds]);

    return (
      <>
        {isFiltering ? (
          <CommandSelectLoading>Searching campaigns and ad sets…</CommandSelectLoading>
        ) : isLoading && filteredCampaigns.length === 0 ? (
          <CommandSelectLoading>Loading campaigns and ad sets...</CommandSelectLoading>
        ) : (
          <>
            {/* Select All/Deselect All */}
            {allVisibleIds.length > 0 && (
              <CommandSelectAll
                checked={selectAllState.checked}
                indeterminate={selectAllState.indeterminate}
                disabled={isLoading || isUpdating}
                onToggle={wrappedHandleSelectAll}
                count={allVisibleIds.length}
              />
            )}

            {filteredCampaigns.length > 0 ? (
              filteredCampaigns.map((campaign) => (
                <div key={campaign.id} className="border-b border-border last:border-b-0">
                  {/* Campaign Header with Checkbox */}
                  <CampaignGroupHeader
                    campaign={campaign}
                    selectedIdsSet={selectedIdsSet}
                    onToggleCampaign={handleToggleCampaign}
                    isUpdating={isUpdating}
                    autoSelectKeywords={autoSelectKeywords}
                  />

                  {/* Ad Sets under this campaign */}
                  {campaign.adSets && campaign.adSets.length > 0 && (
                    <div className="bg-muted/30">
                      {campaign.adSets.map((adSet) => (
                        <CommandSelectItem
                          key={adSet.id}
                          value={adSet.id}
                          onSelect={() => handleSelectItem(adSet.id)}
                          selected={selectedIdsSet.has(adSet.id)}
                          disabled={isLoading || isUpdating}
                          className="pl-8 border-b-0"
                        >
                          <div className="flex flex-col">
                            <span>{adSet.name}</span>
                            <span className="text-xs text-muted-foreground">
                              Ad Set
                              {(memoizedShouldAutoSelect(adSet.name, autoSelectKeywords) ||
                                adSet.hasPaidPartnershipAds) &&
                                ` ⭐ Auto-selected${adSet.hasPaidPartnershipAds ? ' (Paid Partnership)' : ''}`}
                            </span>
                          </div>
                        </CommandSelectItem>
                      ))}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <>
                {!searchQuery || searchQuery.trim() === '' ? (
                  <CommandSelectEmpty>Start typing to search campaigns and ad sets.</CommandSelectEmpty>
                ) : (
                  <CommandSelectEmpty>No campaigns or ad sets match your search.</CommandSelectEmpty>
                )}
              </>
            )}
          </>
        )}
      </>
    );
  },
);

AdCampaignsAndSetsContent.displayName = 'AdCampaignsAndSetsContent';

export const AdCampaignsAndSetsOptions = ({
  adCampaigns = [],
  isLoading = false,
  value,
  onChange,
  autoSelectKeywords = DEFAULT_AUTO_SELECT_KEYWORDS,
}: {
  adCampaigns?: AdCampaign[];
  isLoading?: boolean;
  value: string[];
  onChange: (ids: string[]) => void;
  autoSelectKeywords?: readonly string[];
}) => {
  // Local state management to prevent infinite loops (same pattern as AdAccountsOptions)
  const [selectedIds, setSelectedIds] = React.useState<string[]>(value || []);
  const [isUpdating, setIsUpdating] = React.useState(false);

  // Memoized helper function for set-based array comparison (order-independent)
  const arraysHaveSameElements = React.useCallback((arr1: string[], arr2: string[]): boolean => {
    if (arr1.length !== arr2.length) return false;
    const set1 = new Set(arr1);
    const set2 = new Set(arr2);
    return set1.size === set2.size && [...set1].every((item) => set2.has(item));
  }, []);

  // Memoized value prop for stable comparison
  const memoizedValue = React.useMemo(() => value || [], [value]);

  // Unified update handler with proper state management and change detection
  const updateSelection = React.useCallback(
    (newSelection: string[]) => {
      // Only update if selection actually changed
      if (!arraysHaveSameElements(selectedIds, newSelection)) {
        setIsUpdating(true);
        setSelectedIds(newSelection);
        onChange(newSelection);
      }
    },
    [onChange, selectedIds, arraysHaveSameElements],
  );

  // Optimized useEffect for state synchronization with change detection
  React.useEffect(() => {
    // Only update if arrays are actually different and not currently updating
    if (!isUpdating) {
      const arraysAreEqual = arraysHaveSameElements(selectedIds, memoizedValue);

      if (!arraysAreEqual) {
        setSelectedIds(memoizedValue);
      }
    }

    // Reset isUpdating flag when state synchronization is complete
    if (isUpdating) {
      const arraysAreEqual = arraysHaveSameElements(selectedIds, memoizedValue);

      if (arraysAreEqual) {
        setIsUpdating(false);
      }
    }
  }, [memoizedValue, isUpdating, selectedIds, arraysHaveSameElements]);

  const handleSelectItem = React.useCallback(
    (itemId: string) => {
      if (isUpdating) return;

      // Use Set for O(1) lookups instead of array.includes()
      const selectedIdsSet = new Set(selectedIds);
      let newSelection: string[];

      if (selectedIdsSet.has(itemId)) {
        // Remove item - use Set for O(1) operations
        selectedIdsSet.delete(itemId);
        newSelection = Array.from(selectedIdsSet);
      } else {
        // Add item - use Set for O(1) operations
        selectedIdsSet.add(itemId);
        newSelection = Array.from(selectedIdsSet);
      }

      // Only update if selection actually changed
      if (!arraysHaveSameElements(selectedIds, newSelection)) {
        updateSelection(newSelection);
      }
    },
    [selectedIds, updateSelection, isUpdating, arraysHaveSameElements],
  );

  const handleDismiss = React.useCallback(
    (e: React.SyntheticEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Only update if there are actually selected items to clear
      if (selectedIds.length > 0) {
        updateSelection([]);
      }
    },
    [updateSelection, selectedIds.length],
  );

  // Memoized ad set lookup map for O(1) performance
  const adSetMap = React.useMemo(() => {
    const map = new Map<string, AdSet>();
    adCampaigns.forEach((campaign) => {
      if (campaign.adSets) {
        campaign.adSets.forEach((adSet) => {
          map.set(adSet.id, adSet);
        });
      }
    });
    return map;
  }, [adCampaigns]);

  // Memoized display text calculation for performance optimization
  const displayText = React.useMemo(() => {
    if (selectedIds.length === 0) {
      return { mainText: 'Select campaigns and ad sets...', count: null };
    }

    // Use O(1) map lookup instead of nested loops
    const firstSelectedAdSet = adSetMap.get(selectedIds[0]);

    if (selectedIds.length === 1) {
      return {
        mainText: firstSelectedAdSet?.name || 'Select campaigns and ad sets...',
        count: null,
      };
    }

    // Multiple selections - show first item + count
    return {
      mainText: firstSelectedAdSet?.name || 'Multiple selected',
      count: selectedIds.length - 1,
    };
  }, [selectedIds, adSetMap]);

  return (
    <CommandSelect>
      <CommandSelectTrigger placeholder="Select campaigns and ad sets...">
        <CommandSelectValue
          placeholder="Select campaigns and ad sets..."
          displayText={selectedIds.length > 0 ? displayText.mainText : undefined}
          count={displayText.count}
          onClear={handleDismiss}
          disabled={isUpdating}
        />
      </CommandSelectTrigger>
      <CommandSelectContent searchPlaceholder="Search campaigns and ad sets..." maxHeight="300px">
        <AdCampaignsAndSetsContent
          adCampaigns={adCampaigns}
          isLoading={isLoading}
          selectedIds={selectedIds}
          isUpdating={isUpdating}
          handleSelectItem={handleSelectItem}
          updateSelection={updateSelection}
          autoSelectKeywords={autoSelectKeywords}
        />
      </CommandSelectContent>
    </CommandSelect>
  );
};
